/* ── section-main-article.js ─────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── Helpers ── */
  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Read page data ── */
  function getPageData() {
    const el = document.getElementById('art-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { return null; }
  }

  /* ── Table of Contents ────────────────────────────────────────────────────── */
  function buildTOC() {
    const body = document.getElementById('art-body');
    if (!body) return;

    const headings = Array.from(body.querySelectorAll('h2, h3'));
    if (!headings.length) {
      document.querySelectorAll('#art-toc-list, #art-mobile-toc-list').forEach(list => {
        list.innerHTML = '';
      });
      return;
    }

    const items = headings.map((h, i) => {
      const level = h.tagName.toLowerCase(); // 'h2' or 'h3'
      if (!h.id) {
        h.id = slugify(h.textContent) || (level + '-' + i);
      }
      return { id: h.id, text: h.textContent.trim(), level };
    });

    const makeList = (targetId) => {
      const list = document.getElementById(targetId);
      if (!list) return;
      list.innerHTML = items.map(item => `
        <li class="${item.level === 'h3' ? 'art-toc--h3' : ''}">
          <a href="#${escapeHtml(item.id)}">${escapeHtml(item.text)}</a>
        </li>
      `).join('');
    };

    makeList('art-toc-list');
    makeList('art-mobile-toc-list');

    setupScrollSpy(headings);
  }

  /* ── Scroll spy via IntersectionObserver ── */
  function setupScrollSpy(headings) {
    const rootMargin = () => {
      const hh = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--fixed-storefront-stack-height') || '80', 10);
      return `-${hh + 4}px 0px -70% 0px`;
    };

    let activeId = null;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          activeId = entry.target.id;
          updateActive(activeId);
        }
      });
    }, {
      rootMargin: rootMargin(),
      threshold: 0
    });

    headings.forEach(h => observer.observe(h));

    function updateActive(id) {
      document.querySelectorAll('.art-toc a').forEach(a => {
        const isActive = a.getAttribute('href') === '#' + id;
        a.classList.toggle('is-active', isActive);
      });
    }

    /* Smooth scroll on TOC link click */
    document.querySelectorAll('.art-toc a').forEach(a => {
      a.addEventListener('click', e => {
        e.preventDefault();
        const target = document.getElementById(a.getAttribute('href').slice(1));
        if (!target) return;
        const hh = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--fixed-storefront-stack-height') || '80', 10);
        const top = target.getBoundingClientRect().top + window.scrollY - hh - 20;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* ── Mobile TOC toggle ── */
  function setupMobileTOC() {
    const btn = document.querySelector('.art-toc-toggle');
    const panel = document.getElementById('art-mobile-toc');
    if (!btn || !panel) return;

    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  }

  /* ── Predictive Search fetch ── */
  async function searchProducts(term, limit = 4) {
    const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=${limit}&resources[options][unavailable_products]=hide`;
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.resources?.results?.products || [];
    } catch { return []; }
  }

  async function searchArticles(term, limit = 5) {
    const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=article&resources[limit]=${limit}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.resources?.results?.articles || [];
    } catch { return []; }
  }

  /* ── Build search terms from article context ── */
  function buildSearchTerms() {
    const title = document.querySelector('.art-title')?.textContent || '';
    const body = document.getElementById('art-body');
    const headings = body ? Array.from(body.querySelectorAll('h2')).map(h => h.textContent.trim()) : [];

    const terms = [];

    /* First 4 meaningful words of title */
    const stopWords = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','is','are','was','were','how','what','why','when','which']);
    const titleWords = title.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w))
      .slice(0, 5);

    if (titleWords.length >= 2) {
      terms.push(titleWords.slice(0, 3).join(' '));
    }

    /* First 2 h2 headings (truncated) */
    headings.slice(0, 2).forEach(h => {
      const words = h.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w));
      if (words.length >= 2) terms.push(words.slice(0, 3).join(' '));
    });

    /* Deduplicate */
    return [...new Set(terms)].slice(0, 4);
  }

  /* ── Render product card HTML ── */
  function renderProductCard(p) {
    const img = p.image ? `<img src="${escapeHtml(p.image.src || p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">` : '';
    const comparePrice = p.compare_at_price ? `<s>${escapeHtml(p.compare_at_price)}</s>` : '';
    return `
      <a href="${escapeHtml(p.url)}" class="art-product-card">
        <div class="art-product-card__img">${img}</div>
        <div class="art-product-card__info">
          <p class="art-product-card__title">${escapeHtml(p.title)}</p>
          <p class="art-product-card__price">${escapeHtml(p.price || '')} ${comparePrice}</p>
        </div>
      </a>
    `;
  }

  /* ── Render article item HTML ── */
  function renderArticleItem(a) {
    const date = a.published_at
      ? new Date(a.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : (a.date || '');
    return `
      <li class="art-article-item">
        <a href="${escapeHtml(a.url)}">
          <span class="art-article-item__title">${escapeHtml(a.title)}</span>
          <span class="art-article-item__date">${escapeHtml(date)}</span>
        </a>
      </li>
    `;
  }

  /* ── Load related products ── */
  async function loadRelatedProducts(data) {
    const container = document.getElementById('art-product-list');
    if (!container) return;

    const terms = buildSearchTerms();
    const seen = new Map();

    /* Try each term until we have 4 products */
    for (const term of terms) {
      if (seen.size >= 4) break;
      const results = await searchProducts(term, 6);
      results.forEach(p => {
        if (seen.size < 4 && !seen.has(p.id)) seen.set(p.id, p);
      });
    }

    /* Also try article tags directly */
    if (seen.size < 4 && data.tags?.length) {
      for (const tag of data.tags.slice(0, 2)) {
        if (seen.size >= 4) break;
        const results = await searchProducts(tag, 4);
        results.forEach(p => {
          if (seen.size < 4 && !seen.has(p.id)) seen.set(p.id, p);
        });
      }
    }

    const products = [...seen.values()];

    if (products.length > 0) {
      container.innerHTML = products.map(renderProductCard).join('');
    } else if (data.fallbackProducts?.length) {
      container.innerHTML = data.fallbackProducts.map(p => renderProductCard({
        id: p.id,
        title: p.title,
        url: p.url,
        price: p.price,
        compare_at_price: p.compare_at_price,
        image: p.image ? { src: p.image } : null
      })).join('');
    } else {
      document.getElementById('art-related-products')?.style.setProperty('display', 'none');
    }
  }

  /* ── Load related articles ── */
  async function loadRelatedArticles(data) {
    const container = document.getElementById('art-article-list');
    if (!container) return;

    const terms = data.tags?.length ? data.tags.slice(0, 3) : buildSearchTerms().slice(0, 2);
    const seen = new Map();

    for (const term of terms) {
      if (seen.size >= 4) break;
      const results = await searchArticles(term, 6);
      results.forEach(a => {
        const id = a.id || a.url;
        if (seen.size < 4 && id !== String(data.currentId) && a.url !== window.location.pathname) {
          seen.set(id, a);
        }
      });
    }

    const articles = [...seen.values()];

    if (articles.length > 0) {
      container.innerHTML = articles.map(renderArticleItem).join('');
    } else if (data.fallbackArticles?.length) {
      const fallback = data.fallbackArticles.filter(a => a.id !== data.currentId).slice(0, 4);
      if (fallback.length > 0) {
        container.innerHTML = fallback.map(a => renderArticleItem({
          url: a.url,
          title: a.title,
          date: a.date
        })).join('');
      } else {
        document.getElementById('art-related-articles')?.style.setProperty('display', 'none');
      }
    } else {
      document.getElementById('art-related-articles')?.style.setProperty('display', 'none');
    }
  }

  /* ── Sidebar newsletter + PDF download ── */
  const ART_NL_KEY = 'laki-nl-sub'; /* shared key with blog hub inline newsletter */

  function showArtNlSuccess() {
    const form = document.getElementById('art-nl-form');
    if (!form) return;
    form.innerHTML = `
      <div class="art-nl__ok" role="status">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
        You're subscribed!
      </div>
    `;
  }

  function unlockPdf() {
    const btn  = document.getElementById('art-pdf-btn');
    const gate = document.getElementById('art-pdf-gate');
    if (btn)  btn.classList.remove('is-locked');
    if (gate) {
      gate.style.transition = 'opacity 0.2s ease';
      gate.style.opacity    = '0';
      setTimeout(() => { gate.style.display = 'none'; }, 220);
    }
  }

  function artFormError(form, selector) {
    let el = form.querySelector(selector);
    if (!el) {
      el = document.createElement('p');
      el.className = selector.replace(/^\./, '');
      el.setAttribute('role', 'alert');
      form.appendChild(el);
    }
    el.textContent = 'Something went wrong. Please try again.';
  }

  async function artAjaxSubscribe(form, onSuccess, onError) {
    const input = form.querySelector('[type="email"]');
    if (!input?.checkValidity()) { input?.reportValidity(); return; }
    const btn = form.querySelector('button[type="submit"]');
    const origLabel = btn?.textContent;
    if (btn) { btn.disabled = true; if (btn.textContent.trim()) btn.textContent = '…'; }
    try {
      await fetch(form.action, {
        method:  'POST',
        body:    new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      try { localStorage.setItem(ART_NL_KEY, '1'); } catch (_) {}
      onSuccess();
    } catch {
      if (btn) { btn.disabled = false; if (origLabel) btn.textContent = origLabel; }
      onError(form);
    }
  }

  function initSidebarFeatures() {
    /* Restore subscribed state from localStorage */
    if (localStorage.getItem(ART_NL_KEY)) {
      showArtNlSuccess();
      unlockPdf();
    }

    /* Sidebar newsletter form */
    const nlForm = document.getElementById('art-nl-form');
    if (nlForm) {
      nlForm.addEventListener('submit', e => {
        e.preventDefault();
        artAjaxSubscribe(
          nlForm,
          () => { showArtNlSuccess(); unlockPdf(); },
          f  => artFormError(f, '.art-nl__error')
        );
      });
    }

    /* PDF gate form */
    const pdfForm = document.getElementById('art-pdf-form');
    if (pdfForm) {
      pdfForm.addEventListener('submit', e => {
        e.preventDefault();
        artAjaxSubscribe(
          pdfForm,
          () => { showArtNlSuccess(); unlockPdf(); },
          f  => artFormError(f, '.art-pdf__error')
        );
      });
    }

    /* PDF download button */
    const pdfBtn = document.getElementById('art-pdf-btn');
    if (pdfBtn) {
      pdfBtn.addEventListener('click', () => {
        if (pdfBtn.classList.contains('is-locked')) return;
        window.print();
      });
    }
  }

  /* ── Article body enhancements ── */
  function enhanceArticleBody() {
    const body = document.getElementById('art-body');
    if (!body) return;

    /* Drop cap: only when first paragraph has ≥ 2 sentences */
    const firstP = body.querySelector('p');
    if (firstP) {
      const text = firstP.textContent.trim();
      /* Count sentence boundaries: punctuation followed by whitespace + capital letter */
      const breaks = (text.match(/[.!?]\s+[A-Z]/g) || []).length;
      const endsWithPunct = /[.!?]['"]?$/.test(text);
      const sentences = breaks + (endsWithPunct ? 1 : 0);
      if (sentences >= 2 || text.length > 180) {
        firstP.classList.add('has-drop-cap');
      }
    }

    /* Standalone images: <p> containing only an <img> → breakout class */
    body.querySelectorAll('p').forEach(p => {
      const nodes = Array.from(p.childNodes).filter(n =>
        !(n.nodeType === Node.TEXT_NODE && !n.textContent.trim())
      );
      if (nodes.length === 1 && nodes[0].nodeName === 'IMG') {
        p.classList.add('art-img-block');
      }
    });

    /* Tables → scroll wrapper */
    body.querySelectorAll('table').forEach(table => {
      if (table.closest('.art-table-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'art-table-wrap';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });

    /* iframes → responsive 16:9 wrapper */
    body.querySelectorAll('iframe').forEach(iframe => {
      if (iframe.closest('.art-video-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'art-video-wrap';
      iframe.parentNode.insertBefore(wrap, iframe);
      wrap.appendChild(iframe);
    });
  }

  /* ── Reading progress ring ── */
  function initReadingProgress(data) {
    const ring  = document.getElementById('art-reading-ring');
    const fill  = document.getElementById('art-reading-ring-fill');
    const label = document.getElementById('art-reading-ring-label');
    const body  = document.getElementById('art-body');
    if (!ring || !fill || !body) return;

    const CIRC         = 2 * Math.PI * 18; // 113.097
    const totalMinutes = (data && data.readMinutes) ? data.readMinutes : 0;
    let rafId = null;

    function tick() {
      rafId = null;
      const winH    = window.innerHeight;
      const scrollY = window.scrollY;
      const bodyTop = body.getBoundingClientRect().top + scrollY;
      const bodyH   = body.scrollHeight;

      /* progress: 0 when viewport bottom touches body top → 1 when viewport bottom reaches body bottom */
      const progress = Math.min(Math.max((scrollY + winH - bodyTop) / bodyH, 0), 1);

      /* Vertical position: glides from just-below-header down to just-above-scroll-to-top */
      const hdrH       = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--fixed-storefront-stack-height') || '80'
      );
      const topBound    = hdrH + 16;
      const bottomBound = winH - 126; // 28px margin + 42px btn + 12px gap + 44px ring height
      ring.style.top    = (topBound + progress * (bottomBound - topBound)) + 'px';

      /* Fill the SVG ring */
      fill.style.strokeDashoffset = CIRC * (1 - progress);

      /* Done state */
      const done = progress >= 0.98;
      ring.classList.toggle('is-done', done);

      /* Centre label: remaining minutes → ✓ */
      if (done) {
        label.textContent = '✓';
      } else if (totalMinutes) {
        label.textContent = Math.max(Math.ceil(totalMinutes * (1 - progress)), 1) + 'm';
      } else {
        label.textContent = Math.round(progress * 100) + '%';
      }

      /* Show once user has scrolled into the article */
      ring.classList.toggle('is-active', scrollY + winH > bodyTop + 80 && progress > 0);
    }

    window.addEventListener('scroll', () => {
      if (!rafId) rafId = requestAnimationFrame(tick);
    }, { passive: true });

    requestAnimationFrame(tick);
  }

  /* ── Init ── */
  function init() {
    const data = getPageData();

    buildTOC();
    setupMobileTOC();
    enhanceArticleBody();
    initSidebarFeatures();
    initReadingProgress(data);

    if (data) {
      /* Stagger the two scan passes so they don't race for network slots */
      loadRelatedProducts(data);
      setTimeout(() => loadRelatedArticles(data), 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor reload */
  document.addEventListener('shopify:section:load', e => {
    if (e.target.contains(document.getElementById('art-body'))) init();
  });

  /* ── Scroll-to-top button ── */
  const scrollBtn = document.getElementById('art-scroll-top');
  if (scrollBtn) {
    scrollBtn.removeAttribute('hidden');
    window.addEventListener('scroll', () => {
      scrollBtn.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });
    scrollBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }
})();
